import React, { useState, useEffect, useRef } from 'react';
import useSWR from 'swr';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Switch } from './ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import ActionButton from './ui/action-button';
import { Camera, Search, Check, X, QrCode, Loader2, Users, Calendar, Clock, Clipboard, Mail, CheckCircle, Download } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';
import DashboardPageLayout from './DashboardPageLayout';
import FormSection from './FormSection';
import Pagination from './Pagination';
import jsQR from 'jsqr';
import { QRCodeCanvas } from 'qrcode.react';

const CheckInTab = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [checkInEnabled, setCheckInEnabled] = useState(false);
  const [checkInPin, setCheckInPin] = useState('000000'); // 6-digit default
  
  const [scanMode, setScanMode] = useState('manual'); // 'manual', 'camera', 'search'
  const [searchQuery, setSearchQuery] = useState('');
  const [checkingIn, setCheckingIn] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResultsModal, setShowSearchResultsModal] = useState(false);
  const [searchResultsPage, setSearchResultsPage] = useState(1);
  const searchResultsPerPage = 5;
  
  // QR Code modal for scanner link
  const [showScannerQR, setShowScannerQR] = useState(false);
  const [scannerQRUrl, setScannerQRUrl] = useState('');
  
  // SWR fetcher function
  const fetcher = (url) => api.get(url).then(res => res.data);
  
  // Use SWR for recent check-ins with auto-refresh
  const { data: recentCheckIns = [], error: checkInsError, isLoading: loadingCheckIns, mutate: refreshCheckIns } = useSWR(
    checkInEnabled ? '/bookings/checked-in' : null,
    fetcher,
    {
      refreshInterval: 30000, // Auto-refresh every 30 seconds
      revalidateOnFocus: true, // Refresh when user returns to tab
      dedupingInterval: 5000 // Prevent duplicate requests within 5 seconds
    }
  );
  
  // Email modal state
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailBooking, setEmailBooking] = useState(null);
  const [emailTo, setEmailTo] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  
  // Step 3: Sorting and filtering
  const [sortBy, setSortBy] = useState('recent'); // 'recent', 'name', 'service'
  const [filterDate, setFilterDate] = useState('all'); // 'all', 'today', 'week'
  const [checkInPage, setCheckInPage] = useState(1);
  const checkInsPerPage = 10;
  
  // Selected check-in for details
  const [selectedCheckIn, setSelectedCheckIn] = useState(null);
  const [showCheckInDetails, setShowCheckInDetails] = useState(false);
  
  // Check-in confirmation
  const [showCheckInConfirm, setShowCheckInConfirm] = useState(false);
  const [bookingToCheckIn, setBookingToCheckIn] = useState(null);
  
  // Camera refs
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [scanning, setScanning] = useState(false);
  const scanIntervalRef = useRef(null);

  useEffect(() => {
    fetchSettings();
    
    return () => {
      stopCamera();
    };
  }, []);
  
  const fetchSettings = async () => {
    try {
      const orgId = localStorage.getItem('organizationId');
      const orgResponse = await api.get(`/organizations/${orgId}`);
      const orgData = orgResponse.data;
      
      setCheckInEnabled(orgData.checkInEnabled || false);
      setCheckInPin(orgData.checkInPin || '000000'); // 6-digit default
    } catch (error) {
      console.error('Error fetching check-in settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSave = async () => {
    if (checkInEnabled && (!/^\d{6}$/.test(checkInPin))) {
      toast.error('PIN must be exactly 6 digits');
      return;
    }

    setSaving(true);
    try {
      const orgId = localStorage.getItem('organizationId');
      
      if (!orgId) {
        toast.error('Organization ID not found. Please refresh the page.');
        return;
      }
      
      console.log('Saving check-in settings:', { checkInEnabled, checkInPin, orgId });
      
      const response = await api.put(`/organizations/${orgId}`, {
        checkInEnabled: checkInEnabled,
        checkInPin: checkInPin
      });
      
      console.log('Save response:', response);

      toast.success('Check-in settings saved!');
      
      // Reload settings to confirm
      fetchSettings();
    } catch (error) {
      console.error('Error saving settings:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to save settings';
      toast.error(`Save error: ${errorMsg}`);
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, []);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' } // Use back camera on mobile
      });
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
        setCameraActive(true);
        setScanning(true);
        startScanning();
      }
    } catch (error) {
      console.error('Camera error:', error);
      toast.error('Failed to access camera. Please check permissions.');
    }
  };

  const stopCamera = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      const tracks = videoRef.current.srcObject.getTracks();
      tracks.forEach(track => track.stop());
      videoRef.current.srcObject = null;
    }
    setCameraActive(false);
    setScanning(false);
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current);
    }
  };

  const startScanning = () => {
    scanIntervalRef.current = setInterval(() => {
      if (videoRef.current && canvasRef.current && videoRef.current.readyState === videoRef.current.HAVE_ENOUGH_DATA) {
        const canvas = canvasRef.current;
        const video = videoRef.current;
        
        canvas.height = video.videoHeight;
        canvas.width = video.videoWidth;
        
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const code = jsQR(imageData.data, imageData.width, imageData.height);
        
        if (code) {
          handleQRCodeDetected(code.data);
        }
      }
    }, 300); // Scan every 300ms
  };

  const handleQRCodeDetected = (qrData) => {
    // Extract confirmation code from URL
    // Format: https://domain.com/check-in/CS-ABC123?org=...
    try {
      const url = new URL(qrData);
      const pathParts = url.pathname.split('/');
      const confirmationCode = pathParts[pathParts.length - 1];
      
      if (confirmationCode && confirmationCode.startsWith('CS-')) {
        stopCamera();
        setSearchQuery(confirmationCode);
        toast.success(`QR Code scanned: ${confirmationCode}`);
        // Auto check-in
        setTimeout(() => handleManualCheckIn(), 500);
      }
    } catch (error) {
      // Invalid QR code format
      console.log('Invalid QR code:', qrData);
    }
  };

  const handleCheckInSuccess = () => {
    // Trigger SWR to refetch check-ins after a successful check-in
    refreshCheckIns();
  };

  const handleManualCheckIn = async () => {
    if (!searchQuery.trim()) {
      toast.error('Please enter a booking ID, name, email, or phone');
      return;
    }

    setCheckingIn(true);
    try {
      // Search for booking first
      const searchResponse = await api.get(`/bookings/search-for-checkin`, {
        params: { query: searchQuery }
      });
      
      const bookings = searchResponse.data.bookings || [];
      
      if (bookings.length === 0) {
        toast.error('No booking found with that search');
        return;
      }
      
      // Show all results in modal
      setSearchResults(bookings);
      setSearchResultsPage(1); // Reset to first page
      setShowSearchResultsModal(true); // Open modal
      
      if (bookings.length === 1) {
        toast.success('Found 1 booking!');
      } else {
        toast.success(`Found ${bookings.length} bookings!`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Search failed');
    } finally {
      setCheckingIn(false);
    }
  };

  const checkInBooking = async (bookingId) => {
    const booking = searchResults.find(b => b.id === bookingId);
    if (!booking) {
      toast.error('Booking not found');
      return;
    }
    
    console.log('Check-in booking:', booking);
    
    // Direct check-in without confirmation
    try {
      const response = await api.post(`/bookings/${booking.id}/check-in`);
      
      if (response.data.alreadyCheckedIn) {
        toast.info('Customer already checked in');
      } else {
        toast.success('✅ Customer checked in successfully!');
      }
      
      // Update the booking in search results to show checked-in status
      setSearchResults(prevResults => 
        prevResults.map(b => 
          b.id === booking.id 
            ? { ...b, checkInStatus: 'checked_in', checkInAt: response.data.checkInAt }
            : b
        )
      );
      
      handleCheckInSuccess();
    } catch (error) {
      console.error('Check-in error:', error);
      toast.error(error.response?.data?.detail || 'Check-in failed');
    }
  };
  
  const openEmailModal = (booking) => {
    setEmailBooking(booking);
    setEmailTo(booking.customerEmail || ''); // Pre-fill if available, empty if not
    setShowEmailModal(true);
  };
  
  const sendTicketEmail = async () => {
    if (!emailTo || !emailTo.trim()) {
      toast.error('Please enter customer email address');
      return;
    }
    
    setSendingEmail(true);
    try {
      const ticketUrl = `${window.location.origin}/customer-ticket/${emailBooking.confirmationCode}`;
      const orgName = localStorage.getItem('organizationName') || 'BookingFlows';
      
      // Fixed template - no customization
      const subject = `Your Booking Ticket - ${orgName}`;
      const message = `Hi ${emailBooking.customerName || 'there'},\n\nHere's your booking ticket:\n\n${ticketUrl}\n\nSee you soon!\n\n${orgName}`;
      
      await api.post('/emails/send-to-customer', {
        email: emailTo,
        subject: subject,
        message: message
      });
      
      toast.success('✅ Email sent successfully!');
      setShowEmailModal(false);
      setEmailBooking(null);
      setEmailTo('');
    } catch (error) {
      console.error('Email error:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to send email';
      if (errorMsg.includes('rate limit') || errorMsg.includes('too many')) {
        toast.error('Rate limit reached: Maximum 10 emails per customer per day');
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setSendingEmail(false);
    }
  };
  
  const confirmCheckIn = async () => {
    try {
      const response = await api.post(`/bookings/${bookingToCheckIn.id}/check-in`);
      
      if (response.data.alreadyCheckedIn) {
        toast.info('Customer already checked in');
      } else {
        toast.success('Customer checked in successfully!');
      }
      
      // Update the booking in search results to show checked-in status
      setSearchResults(prevResults => 
        prevResults.map(booking => 
          booking.id === bookingToCheckIn.id 
            ? { ...booking, checkInStatus: 'checked_in', checkInAt: response.data.checkInAt }
            : booking
        )
      );
      
      setShowCheckInConfirm(false);
      setBookingToCheckIn(null);
      handleCheckInSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Check-in failed');
    }
  };

  const formatDateTime = (dateStr, timeStr) => {
    const date = new Date(dateStr + 'T' + timeStr);
    return date.toLocaleString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  // Filter and sort check-ins
  const filteredCheckIns = recentCheckIns.filter(booking => {
    if (filterDate === 'today') {
      const today = new Date().toISOString().split('T')[0];
      return booking.checkInAt && booking.checkInAt.startsWith(today);
    }
    if (filterDate === 'week') {
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      return booking.checkInAt && new Date(booking.checkInAt) >= weekAgo;
    }
    return true; // 'all'
  }).sort((a, b) => {
    if (sortBy === 'name') {
      return a.customerName.localeCompare(b.customerName);
    }
    if (sortBy === 'service') {
      return (a.serviceName || '').localeCompare(b.serviceName || '');
    }
    // 'recent' - default
    return new Date(b.checkInAt) - new Date(a.checkInAt);
  });
  
  // Pagination
  const totalCheckInPages = Math.ceil(filteredCheckIns.length / checkInsPerPage);
  const paginatedCheckIns = filteredCheckIns.slice(
    (checkInPage - 1) * checkInsPerPage,
    checkInPage * checkInsPerPage
  );

  return (
    <DashboardPageLayout
      title="Check-In System"
      subtitle="QR code scanning and customer check-in management"
      actions={
        <ActionButton
          onClick={handleSave}
          loading={saving}
          disabled={checkInEnabled && !/^\d{6}$/.test(checkInPin)}
          icon={CheckCircle}
        >
          Save Settings
        </ActionButton>
      }
    >

      {loading ? (
        <div className="text-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto" />
        </div>
      ) : (
        <div className="space-y-8">
          {/* STEP 1: Enable/Disable */}
          <FormSection
            stepNumber={1}
            title="Enable Check-In System"
            description="Toggle QR code scanning for customer tickets"
            color="#8B5CF6"
          >
            <div className="space-y-4">
              {/* Toggle */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-md border border-gray-200">
                <div>
                  <p className="text-base font-semibold">Enable Check-In System</p>
                  <p className="text-base text-gray-600 mt-1">
                    Show QR code on customer tickets for staff scanning
                  </p>
                </div>
                <button
                  onClick={() => setCheckInEnabled(!checkInEnabled)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    checkInEnabled ? 'bg-purple-600' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      checkInEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {/* PIN Configuration */}
              {checkInEnabled && (
                <div>
                  <label className="block text-base font-semibold text-gray-700 mb-2">
                    Staff Check-In PIN <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={checkInPin}
                    onChange={(e) => setCheckInPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    maxLength={6}
                    className="max-w-xs font-mono text-lg text-center h-12"
                  />
                  <p className="text-base text-gray-600 mt-2">
                    6-digit PIN for secure scanner access (tokens expire after 8 hours)
                  </p>
                </div>
              )}

              {/* Info Box */}
              {checkInEnabled ? (
                <div className="bg-purple-50 border border-purple-200 rounded-md p-4">
                  <p className="text-base text-purple-900 font-semibold mb-2">Check-In Active</p>
                  <p className="text-base text-purple-800">
                    QR codes will appear on all customer tickets. Staff can scan to check in customers.
                  </p>
                </div>
              ) : (
                <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
                  <p className="text-base text-gray-700">
                    Check-in disabled. Great for services that don't need attendance tracking (barbershops, consultations).
                  </p>
                </div>
              )}
            </div>
          </FormSection>

          {/* STEP 2: Check In Methods */}
          {checkInEnabled && (
            <FormSection
              stepNumber={2}
              title="Check In Customer"
              description="Choose a method to check in customers"
              color="#3B82F6"
            >
              {/* Mode Selector - 3 OPTIONS */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                {/* Option 1: QR Scanner Gun */}
                <div className="p-6 rounded-lg border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-purple-100">
                  <div className="flex justify-center mb-3">
                    <div className="p-3 bg-purple-600 rounded-full">
                      <QrCode className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <div className="font-bold text-base mb-2 text-center text-gray-900">QR Scanner Gun</div>
                  <p className="text-sm text-gray-600 mb-4 text-center">Secure 8-hour session token</p>
                  <div className="space-y-2">
                    <button
                      onClick={async () => {
                        try {
                          const response = await api.post('/check-in/create-scanner-session');
                          const { token, expiresAt } = response.data;
                          
                          const scannerUrl = `${window.location.origin}/qr-code-scanner?token=${token}`;
                          window.open(scannerUrl, '_blank');
                          
                          const expiry = new Date(expiresAt).toLocaleTimeString();
                          toast.success(`Scanner opened! Expires at ${expiry}`);
                        } catch (error) {
                          console.error('Error creating scanner session:', error);
                          toast.error('Failed to open scanner');
                        }
                      }}
                      className="w-full py-2.5 bg-purple-600 text-white text-sm font-semibold rounded-lg hover:bg-purple-700 transition shadow-sm"
                    >
                      Open Scanner
                    </button>
                    <button
                      onClick={async () => {
                        try {
                          const response = await api.post('/check-in/create-scanner-session');
                          const { token, expiresAt } = response.data;
                          
                          const scannerUrl = `${window.location.origin}/qr-code-scanner?token=${token}`;
                          await navigator.clipboard.writeText(scannerUrl);
                          
                          const expiry = new Date(expiresAt).toLocaleTimeString();
                          toast.success(`Scanner link copied! Share with staff (expires at ${expiry})`);
                        } catch (error) {
                          console.error('Error creating scanner link:', error);
                          toast.error('Failed to create scanner link');
                        }
                      }}
                      className="w-full py-2.5 bg-white text-purple-700 text-sm font-semibold rounded-lg hover:bg-purple-50 transition border-2 border-purple-300 flex items-center justify-center gap-2"
                    >
                      <Clipboard className="w-4 h-4" />
                      Copy Link
                    </button>
                    <button
                      onClick={async () => {
                        try {
                          const response = await api.post('/check-in/create-scanner-session');
                          const { token, expiresAt } = response.data;
                          
                          const scannerUrl = `${window.location.origin}/qr-code-scanner?token=${token}`;
                          setScannerQRUrl(scannerUrl);
                          setShowScannerQR(true);
                          
                          toast.success('QR Code ready! Staff can scan to open scanner on their phone');
                        } catch (error) {
                          console.error('Error creating scanner QR:', error);
                          toast.error('Failed to generate QR code');
                        }
                      }}
                      className="w-full py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm font-semibold rounded-lg hover:from-purple-700 hover:to-pink-700 transition shadow-md flex items-center justify-center gap-2"
                    >
                      <QrCode className="w-4 h-4" />
                      Show QR Code
                    </button>
                  </div>
                </div>
                
                {/* Option 2: Scan with Phone */}
                <div className="p-6 rounded-lg border-2 border-green-200 bg-gradient-to-br from-green-50 to-green-100 flex flex-col items-center justify-center min-h-[200px]">
                  <div className="flex justify-center mb-3">
                    <div className="p-3 bg-green-600 rounded-full">
                      <Camera className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <div className="font-bold text-base mb-2 text-center text-gray-900">Scan with Phone</div>
                  <p className="text-sm text-gray-600 text-center">Staff scans QR code on customer's ticket, then enters PIN</p>
                </div>
                
                {/* Option 3: Manual Lookup */}
                <button
                  onClick={() => setScanMode('manual')}
                  className={`p-6 rounded-lg border-2 transition-all text-left ${
                    scanMode === 'manual'
                      ? 'border-blue-500 bg-blue-50 shadow-md'
                      : 'border-blue-200 bg-gradient-to-br from-blue-50 to-blue-100 hover:border-blue-400'
                  }`}
                >
                  <div className="flex justify-center mb-3">
                    <div className={`p-3 rounded-full ${scanMode === 'manual' ? 'bg-blue-600' : 'bg-blue-500'}`}>
                      <Search className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <div className="font-bold text-base mb-2 text-center text-gray-900">Manual Lookup</div>
                  <p className="text-sm text-gray-600 text-center">Search by ID or name</p>
                </button>
              </div>

          {/* Manual Lookup Mode - Simplified */}
          {scanMode === 'manual' && (
            <div className="space-y-4">
              <div className="flex gap-2">
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search booking ID, name, email, or phone..."
                  className="flex-1 h-12 text-base"
                  onKeyPress={(e) => e.key === 'Enter' && handleManualCheckIn()}
                />
                <Button
                  onClick={handleManualCheckIn}
                  disabled={checkingIn || !searchQuery.trim()}
                  className="h-12 px-6 bg-blue-600 hover:bg-blue-700"
                >
                  {checkingIn ? (
                    <><Loader2 className="w-5 h-5 animate-spin" /></>
                  ) : (
                    <>
                      <Search className="w-5 h-5 mr-2" />
                      Search
                    </>
                  )}
                </Button>
              </div>
              
              {/* Self Check-In Portal Link */}
              <p className="text-sm text-gray-600">
                💡 Tip: You can also direct customers to check themselves in online if you prefer a{' '}
                <a 
                  href="/self-check-in" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-700"
                >
                  self-service check-in
                </a> process.
              </p>
            </div>
          )}
          </FormSection>
        )}

          {/* STEP 3: Recent Check-Ins - Simplified */}
          {checkInEnabled && (
            <FormSection
              stepNumber={3}
              title="Recent Check-Ins"
              description={`Showing ${recentCheckIns.length} check-ins`}
              color="#10B981"
            >
            <div className="mb-4">
              <Button
                onClick={() => refreshCheckIns()}
                size="sm"
                variant="outline"
                className="flex items-center gap-2"
                disabled={loadingCheckIns}
              >
                <Loader2 className={`w-4 h-4 ${loadingCheckIns ? 'animate-spin' : ''}`} />
                {loadingCheckIns ? 'Refreshing...' : 'Refresh'}
              </Button>
            </div>
            
            {recentCheckIns.length === 0 ? (
              <div className="p-8 text-center bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                <QrCode className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 font-medium">No check-ins yet</p>
                <p className="text-sm text-gray-400 mt-1">Scan your first customer to see them here</p>
              </div>
            ) : (
              <div className="bg-gray-100 border border-gray-200 rounded-lg p-4 max-h-96 overflow-y-auto">
                <div className="space-y-2">
                  {recentCheckIns.slice(0, 20).map((booking) => (
                    <div
                      key={booking.id}
                      className="p-3 rounded-md bg-green-900/10 border border-green-700"
                    >
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-900 truncate">
                            {booking.customerName}
                          </p>
                          <p className="text-xs text-gray-600 truncate">{booking.serviceName}</p>
                          <p className="text-xs text-gray-500 font-mono">{booking.confirmationCode}</p>
                        </div>
                        <span className="text-xs text-gray-500 flex-shrink-0">
                          {booking.checkInAt ? new Date(booking.checkInAt).toLocaleTimeString() : '-'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </FormSection>
        )}
        </div>
      )}

      {/* Check-In Details Modal */}
        <Dialog open={showCheckInDetails} onOpenChange={setShowCheckInDetails}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Check-In Details</DialogTitle>
            </DialogHeader>
            
            {selectedCheckIn && (
              <div className="space-y-4">
                {/* Customer Info */}
                <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Customer</p>
                  <p className="font-bold text-lg text-gray-900">{selectedCheckIn.customerName}</p>
                  {selectedCheckIn.customerPhone && (
                    <p className="text-sm text-gray-600 mt-1">📞 {selectedCheckIn.customerPhone}</p>
                  )}
                </div>

                {/* Booking Info - Only show if data exists */}
                {(selectedCheckIn.preferredDate || selectedCheckIn.preferredTime) && (
                  <div className="grid grid-cols-2 gap-4">
                    {selectedCheckIn.preferredDate && (
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Date</p>
                        <p className="font-semibold text-gray-900">{selectedCheckIn.preferredDate}</p>
                      </div>
                    )}
                    {selectedCheckIn.preferredTime && (
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Time</p>
                        <p className="font-semibold text-gray-900">{selectedCheckIn.preferredTime}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Service - Only if exists */}
                {selectedCheckIn.serviceName && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Service</p>
                    <p className="font-semibold text-gray-900">{selectedCheckIn.serviceName}</p>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  {selectedCheckIn.confirmationCode && (
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Booking ID</p>
                      <p className="font-mono font-bold text-blue-600">{selectedCheckIn.confirmationCode}</p>
                    </div>
                  )}
                  {selectedCheckIn.partySize && (
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Party Size</p>
                      <p className="font-semibold text-gray-900">{selectedCheckIn.partySize} guests</p>
                    </div>
                  )}
                </div>

                {/* Check-In Info - Only show if actually checked in */}
                {selectedCheckIn.checkInStatus === 'checked_in' ? (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <p className="text-xs text-purple-700 uppercase tracking-wide mb-2">Check-In Status</p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-purple-600" />
                        <span className="font-semibold text-purple-900">Checked In</span>
                      </div>
                      {selectedCheckIn.checkInAt && (
                        <div className="text-right">
                          <p className="text-sm font-medium text-purple-800">
                            {new Date(selectedCheckIn.checkInAt).toLocaleString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              hour: 'numeric',
                              minute: '2-digit'
                            })}
                          </p>
                          <p className="text-xs text-purple-600 capitalize">
                            via {selectedCheckIn.checkInMethod || 'manual'}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <p className="text-xs text-yellow-700 uppercase tracking-wide mb-2">Check-In Status</p>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-yellow-600" />
                      <span className="font-semibold text-yellow-900">Not Checked In</span>
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2 pt-2">
                  <Button
                    onClick={() => {
                      const ticketUrl = `${window.location.origin}/customer-ticket/${selectedCheckIn.confirmationCode}`;
                      navigator.clipboard.writeText(ticketUrl);
                      toast.success('Ticket link copied!');
                    }}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    Copy Ticket Link
                  </Button>
                  <Button
                    onClick={() => setShowCheckInDetails(false)}
                    variant="outline"
                    className="flex-1"
                  >
                    Close
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
          
        {/* Check-In Confirmation Modal */}
        <Dialog open={showCheckInConfirm} onOpenChange={setShowCheckInConfirm}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Confirm Check-In</DialogTitle>
            </DialogHeader>
            
            {bookingToCheckIn && (
              <div className="space-y-4">
                <p className="text-lg font-semibold text-gray-900">
                  {bookingToCheckIn.customerName}
                </p>
                <p className="text-gray-600">
                  {bookingToCheckIn.serviceName || 'Service'}
                </p>
                {(bookingToCheckIn.date || bookingToCheckIn.preferredDate) && (
                  <p className="text-sm text-gray-600">
                    {bookingToCheckIn.date || bookingToCheckIn.preferredDate} at {bookingToCheckIn.startTime || bookingToCheckIn.preferredTime}
                  </p>
                )}
                {bookingToCheckIn.groupSize && (
                  <p className="text-sm font-medium text-gray-700">
                    Party size: {bookingToCheckIn.groupSize} guests
                  </p>
                )}
                
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="font-medium text-yellow-900 mb-2">Before checking in:</p>
                  <p className="text-sm text-yellow-800">Is the customer present for this booking?</p>
                </div>

                <div className="flex gap-2 pt-2">
                  <Button
                    onClick={() => setShowCheckInConfirm(false)}
                    variant="outline"
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={confirmCheckIn}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                  >
                    Yes, Check In
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      
      {/* Search Results Modal */}
      <Dialog open={showSearchResultsModal} onOpenChange={setShowSearchResultsModal}>
        <DialogContent className="max-w-[95vw] max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-xl">Booking Lookup Results</DialogTitle>
            <div className="pt-2">
              <div className="flex gap-2 items-center">
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Look up booking ID, customer name, email, phone..."
                  className="flex-1"
                  onKeyPress={(e) => e.key === 'Enter' && handleManualCheckIn()}
                />
                <Button
                  onClick={handleManualCheckIn}
                  disabled={checkingIn || !searchQuery.trim()}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {checkingIn ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <Search className="w-4 h-4 mr-2" />
                      Search
                    </>
                  )}
                </Button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Found {searchResults.length} booking(s) - Showing page {searchResultsPage} of {Math.ceil(searchResults.length / searchResultsPerPage)}
              </p>
            </div>
          </DialogHeader>
          
          <div className="flex-1 overflow-auto">
            <div className="overflow-x-auto">
              <table className="w-full text-xs border-collapse">
                <thead className="bg-gray-100 sticky top-0 z-10">
                  <tr className="border-b-2 border-gray-300">
                    <th className="p-2 text-left font-semibold whitespace-nowrap border-r">Status</th>
                    <th className="p-2 text-left font-semibold whitespace-nowrap border-r">Booking ID</th>
                    <th className="p-2 text-left font-semibold whitespace-nowrap border-r">Customer Name</th>
                    <th className="p-2 text-left font-semibold whitespace-nowrap border-r">Email</th>
                    <th className="p-2 text-left font-semibold whitespace-nowrap border-r">Phone</th>
                    <th className="p-2 text-left font-semibold whitespace-nowrap border-r">Service</th>
                    <th className="p-2 text-left font-semibold whitespace-nowrap border-r">Date</th>
                    <th className="p-2 text-left font-semibold whitespace-nowrap border-r">Time</th>
                    <th className="p-2 text-left font-semibold whitespace-nowrap border-r">Timezone</th>
                    <th className="p-2 text-left font-semibold whitespace-nowrap border-r">Duration</th>
                    <th className="p-2 text-left font-semibold whitespace-nowrap border-r">Party Size</th>
                    <th className="p-2 text-left font-semibold whitespace-nowrap border-r">Payment</th>
                    <th className="p-2 text-left font-semibold whitespace-nowrap border-r">Amount</th>
                    <th className="p-2 text-left font-semibold whitespace-nowrap border-r">Check-In Time</th>
                    <th className="p-2 text-left font-semibold whitespace-nowrap border-r">Customer Ticket</th>
                    <th className="p-2 text-center font-semibold whitespace-nowrap">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {searchResults
                    .slice(
                      (searchResultsPage - 1) * searchResultsPerPage,
                      searchResultsPage * searchResultsPerPage
                    )
                    .map((booking) => (
                      <tr 
                        key={booking.id} 
                        className={`border-b hover:bg-gray-50 ${
                          booking.checkInStatus === 'checked_in' ? 'bg-blue-50' : ''
                        }`}
                      >
                        {/* Status */}
                        <td className="p-2 border-r">
                          {booking.checkInStatus === 'checked_in' ? (
                            <div className="flex items-center gap-1 px-2 py-1 bg-blue-100 border border-blue-300 text-blue-700 rounded-full text-xs font-semibold w-fit whitespace-nowrap">
                              <Check className="w-3 h-3" />
                              Checked In
                            </div>
                          ) : (
                            <div className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-semibold w-fit whitespace-nowrap">
                              Not Checked In
                            </div>
                          )}
                        </td>
                        
                        {/* Booking ID */}
                        <td className="p-2 border-r">
                          <p className="font-mono text-xs font-semibold whitespace-nowrap">
                            {booking.confirmationCode || '-'}
                          </p>
                        </td>
                        
                        {/* Customer Name */}
                        <td className="p-2 border-r">
                          <p className="font-semibold whitespace-nowrap">{booking.customerName || '-'}</p>
                        </td>
                        
                        {/* Email */}
                        <td className="p-2 border-r">
                          <p className="text-xs text-gray-700 max-w-[200px] truncate" title={booking.customerEmail}>
                            {booking.customerEmail || '-'}
                          </p>
                        </td>
                        
                        {/* Phone */}
                        <td className="p-2 border-r">
                          <p className="text-xs text-gray-700 whitespace-nowrap">
                            {booking.customerPhone || '-'}
                          </p>
                        </td>
                        
                        {/* Service */}
                        <td className="p-2 border-r">
                          <p className="text-xs max-w-[150px] truncate" title={booking.serviceName}>
                            {booking.serviceName || '-'}
                          </p>
                        </td>
                        
                        {/* Date */}
                        <td className="p-2 border-r">
                          <p className="text-xs font-medium whitespace-nowrap">
                            {booking.date || booking.preferredDate || '-'}
                          </p>
                        </td>
                        
                        {/* Time */}
                        <td className="p-2 border-r">
                          <p className="text-xs whitespace-nowrap">
                            {booking.startTime || booking.preferredTime || '-'}
                          </p>
                        </td>
                        
                        {/* Timezone */}
                        <td className="p-2 border-r">
                          <p className="text-xs text-gray-600 whitespace-nowrap">
                            {booking.timezone || '-'}
                          </p>
                        </td>
                        
                        {/* Duration */}
                        <td className="p-2 border-r">
                          <p className="text-xs whitespace-nowrap">
                            {booking.duration ? `${booking.duration} min` : '-'}
                          </p>
                        </td>
                        
                        {/* Party Size */}
                        <td className="p-2 border-r">
                          <p className="text-xs text-center">
                            {booking.groupSize || booking.partySize || '-'}
                          </p>
                        </td>
                        
                        {/* Payment Status */}
                        <td className="p-2 border-r">
                          <div className={`px-2 py-1 rounded-full text-xs font-semibold w-fit whitespace-nowrap ${
                            booking.paymentStatus === 'paid' 
                              ? 'bg-green-100 text-green-700' 
                              : booking.paymentStatus === 'unpaid'
                              ? 'bg-yellow-100 text-yellow-700'
                              : 'bg-gray-100 text-gray-600'
                          }`}>
                            {booking.paymentStatus || 'N/A'}
                          </div>
                        </td>
                        
                        {/* Amount */}
                        <td className="p-2 border-r">
                          <p className="text-xs font-semibold whitespace-nowrap">
                            {booking.totalAmount || booking.amountPaid 
                              ? `$${(booking.totalAmount || booking.amountPaid).toFixed(2)}` 
                              : '-'}
                          </p>
                        </td>
                        
                        {/* Check-In Timestamp */}
                        <td className="p-2 border-r">
                          {booking.checkInAt ? (
                            <div className="text-xs">
                              <p className="font-medium whitespace-nowrap">
                                {new Date(booking.checkInAt).toLocaleDateString()}
                              </p>
                              <p className="text-gray-600 whitespace-nowrap">
                                {new Date(booking.checkInAt).toLocaleTimeString()}
                              </p>
                            </div>
                          ) : (
                            <p className="text-xs text-gray-400">-</p>
                          )}
                        </td>
                        
                        {/* Customer Ticket Link */}
                        <td className="p-2 border-r">
                          <div className="flex flex-col gap-1">
                            <Button
                              onClick={() => {
                                const ticketUrl = `${window.location.origin}/customer-ticket/${booking.confirmationCode}`;
                                navigator.clipboard.writeText(ticketUrl);
                                toast.success('Ticket link copied!');
                              }}
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs whitespace-nowrap flex items-center gap-1"
                            >
                              <Clipboard className="w-3 h-3" />
                              Copy Link
                            </Button>
                            <Button
                              onClick={() => openEmailModal(booking)}
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs whitespace-nowrap flex items-center gap-1"
                            >
                              <Mail className="w-3 h-3" />
                              Email Link
                            </Button>
                          </div>
                        </td>
                        
                        {/* Action */}
                        <td className="p-2 text-center">
                          {booking.checkInStatus !== 'checked_in' ? (
                            <Button
                              onClick={() => checkInBooking(booking.id)}
                              size="sm"
                              className="bg-green-600 hover:bg-green-700 h-8 whitespace-nowrap"
                            >
                              <Check className="w-4 h-4 mr-1" />
                              Check In
                            </Button>
                          ) : (
                            <span className="text-xs text-blue-600 font-semibold">✓ Done</span>
                          )}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
          
          {/* Pagination */}
          {searchResults.length > searchResultsPerPage && (
            <div className="flex items-center justify-center gap-2 pt-4 border-t">
              <Button
                onClick={() => setSearchResultsPage(p => Math.max(1, p - 1))}
                disabled={searchResultsPage === 1}
                variant="outline"
                size="sm"
              >
                Previous
              </Button>
              
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.ceil(searchResults.length / searchResultsPerPage) }, (_, i) => i + 1).map(pageNum => (
                  <button
                    key={pageNum}
                    onClick={() => setSearchResultsPage(pageNum)}
                    className={`w-8 h-8 rounded-md text-sm font-medium transition ${
                      searchResultsPage === pageNum
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {pageNum}
                  </button>
                ))}
              </div>
              
              <Button
                onClick={() => setSearchResultsPage(p => Math.min(Math.ceil(searchResults.length / searchResultsPerPage), p + 1))}
                disabled={searchResultsPage === Math.ceil(searchResults.length / searchResultsPerPage)}
                variant="outline"
                size="sm"
              >
                Next
              </Button>
            </div>
          )}
          
          <div className="flex gap-2 pt-4 border-t">
            <Button
              onClick={() => {
                setShowSearchResultsModal(false);
              }}
              variant="outline"
              className="flex-1"
            >
              Close
            </Button>
            <Button
              onClick={() => {
                setSearchResults([]);
                setSearchQuery('');
                setSearchResultsPage(1);
                setShowSearchResultsModal(false);
              }}
              className="flex-1 bg-blue-600 hover:bg-blue-700"
            >
              Clear & Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Email Modal */}
      <Dialog open={showEmailModal} onOpenChange={setShowEmailModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Send Ticket via Email</DialogTitle>
          </DialogHeader>
          
          {emailBooking && (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm font-semibold text-blue-900 mb-1">
                  {emailBooking.customerName || 'Customer'}
                </p>
                <p className="text-xs text-blue-700">
                  Booking: {emailBooking.confirmationCode}
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Customer Email <span className="text-red-500">*</span>
                </label>
                <Input
                  type="email"
                  value={emailTo}
                  onChange={(e) => setEmailTo(e.target.value)}
                  placeholder="Enter customer email..."
                  className="w-full"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {emailBooking.customerEmail ? 'Pre-filled from booking' : 'No email on file - ask customer for their email'}
                </p>
              </div>
              
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <p className="text-xs font-semibold text-gray-700 mb-2">Email Preview:</p>
                <p className="text-xs text-gray-600 whitespace-pre-line">
                  Hi {emailBooking.customerName || 'there'},
                  {'\n\n'}
                  Here's your booking ticket:
                  {'\n'}
                  [Ticket Link]
                  {'\n\n'}
                  See you soon!
                  {'\n\n'}
                  {localStorage.getItem('organizationName') || 'BookingFlows'}
                </p>
                <p className="text-xs text-gray-500 mt-2 italic">
                  Fixed template - ensures professional communication
                </p>
              </div>
              
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-xs text-yellow-800">
                  <strong>Rate Limit:</strong> Maximum 10 emails per customer per day
                </p>
              </div>
              
              <div className="flex gap-2 pt-2">
                <Button
                  onClick={() => {
                    setShowEmailModal(false);
                    setEmailTo('');
                  }}
                  variant="outline"
                  className="flex-1"
                  disabled={sendingEmail}
                >
                  Cancel
                </Button>
                <Button
                  onClick={sendTicketEmail}
                  disabled={sendingEmail || !emailTo.trim()}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                >
                  {sendingEmail ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Mail className="w-4 h-4 mr-2" />
                      Send Email
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Scanner QR Code Modal */}
      <Dialog open={showScannerQR} onOpenChange={setShowScannerQR}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div className="p-2 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg">
                <QrCode className="w-5 h-5 text-white" />
              </div>
              Scan to Open Scanner
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Staff can scan this QR code with their phone camera to instantly open the scanner interface
            </p>
            
            {/* QR Code */}
            <div className="bg-white border-4 border-gray-100 rounded-xl p-6 flex items-center justify-center">
              {scannerQRUrl && (
                <QRCodeCanvas
                  value={scannerQRUrl}
                  size={280}
                  level="H"
                  includeMargin={true}
                />
              )}
            </div>
            
            {/* Info */}
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
              <p className="text-xs text-purple-900 font-medium mb-1">⏱️ Session Info</p>
              <p className="text-xs text-purple-800">
                Scanner session valid for 8 hours. Staff can scan this QR code to check in customers on their phone.
              </p>
            </div>
            
            {/* URL Preview */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs text-gray-500">Scanner URL:</p>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(scannerQRUrl);
                    toast.success('URL copied!');
                  }}
                  className="text-xs text-purple-600 hover:text-purple-700 font-medium flex items-center gap-1"
                >
                  <Clipboard className="w-3.5 h-3.5" />
                  Copy
                </button>
              </div>
              <div className="bg-gray-100 rounded-lg p-2 font-mono text-xs text-gray-700 break-all">
                {scannerQRUrl}
              </div>
            </div>
            
            {/* Buttons */}
            <div className="flex gap-3 pt-2">
              <Button
                onClick={() => setShowScannerQR(false)}
                variant="outline"
                className="flex-1"
              >
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardPageLayout>
  );
};

export default CheckInTab;
